import React, { useState, useEffect } from "react";
import { Users, Search, ChevronRight, Download, FileSpreadsheet, Calendar } from "lucide-react";
import CommonButton from "../../../components/CommonButton";
import { useLazyGetCustomersForAdminQuery } from "../../../redux/actions/Admin/abandonedCartAdmin";
import { Customer360Console } from "./Customer360Console";
import { useAlert } from "../../../components/customer/AlertContext";
import { ADMIN_CUSTOMERS_REWARD_TRANSACTIONS_EXPORT, ADMIN_CUSTOMERS_WALLET_TRANSACTIONS_EXPORT } from "../../../config/apiUrlsAdmin";

export const PetlandCustomerAdminScreen: React.FC = () => {
  const { showConfirm } = useAlert();
  const [triggerFetchCustomers, { data: customerList, isLoading }] = useLazyGetCustomersForAdminQuery();
  const [search, setSearch] = useState("");
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [page, setPage] = useState(1);
  const pageSize = 50;

  useEffect(() => {
    triggerFetchCustomers({
      page,
      pageSize,
      search: search || undefined,
      isActive: true,
    });
  }, [page, search, triggerFetchCustomers]);

  const totalRecords = customerList?.data?.totalRecords ?? 0;
  const totalPages = Math.ceil(totalRecords / pageSize);

  const formatDate = (dateString?: string) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const handleGlobalExport = async (type: "Wallet" | "Rewards") => {
    try {
      const ENDPOINT = type === "Wallet"
        ? ADMIN_CUSTOMERS_WALLET_TRANSACTIONS_EXPORT
        : ADMIN_CUSTOMERS_REWARD_TRANSACTIONS_EXPORT;

      const response = await fetch(ENDPOINT, {
        method: "GET",
        credentials: "include",
      });

      if (!response.ok) {
        throw new Error(`Failed to download ${type.toLowerCase()} statements.`);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `Petland_All_Customers_${type}_Transactions_${new Date().toISOString().slice(0, 10)}.xlsx`;

      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      showConfirm({
        type: "success",
        title: "Download Successful",
        message: `All customer ${type.toLowerCase()} transactions downloaded successfully.`,
        confirmText: "OK",
        showCancel: false,
      });
    } catch (error) {
      console.error("Download failed:", error);
      showConfirm({
        type: "error",
        title: "Download Failed",
        message: "Something went wrong while trying to download the file.",
        confirmText: "OK",
        showCancel: false,
      });
    }
  };

  return (
    <div className="min-h-screen bg-white px-5 py-6 relative overflow-x-hidden">
      <div className="max-w-7xl mx-auto">

        {/* Header Section */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between pb-6 border-b border-gray-100 mb-8 gap-4">
          <div className="text-left">
            <h1 className="text-2xl sm:text-3xl font-black text-logoHeading">
              Customer Management
            </h1>
            <p className="text-sm text-iconColor mt-1">
              View customer profiles, update wallet balances, manage petland points, and check recent activity.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <CommonButton
              text="Export All Wallets"
              variant="outline"
              icon={<Download className="w-4 h-4" />}
              className="border-emerald-200 text-emerald-700 font-bold text-xs bg-emerald-50/50 hover:bg-emerald-50 px-4 py-2 rounded-xl shadow-sm"
              onClick={() => handleGlobalExport("Wallet")}
            />
            <CommonButton
              text="Export All Petland points"
              variant="outline"
              icon={<FileSpreadsheet className="w-4 h-4" />}
              className="border-mainBrandColor/30 text-mainBrandColor font-bold text-xs bg-accent/10 hover:bg-accent/20 px-4 py-2 rounded-xl shadow-sm"
              onClick={() => handleGlobalExport("Rewards")}
            />
          </div>
        </div>

        {!selectedUserId ? (
          <>
            <div className="w-full max-w-md relative mb-6">
              <Search className="w-4 h-4 text-iconColor absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                placeholder="Search customers by name or contact details..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                className="w-full pl-9 pr-3 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-mainBrandColor bg-[#f9f9f9]"
              />
            </div>

            <div className="bg-[#f9f9f9] rounded-[2rem] border border-accent/30 shadow-lg p-6 min-h-[500px]">
              <div className="flex items-center gap-4 mb-6 text-left">
                <div className="w-12 h-12 rounded-2xl bg-accent/20 flex items-center justify-center shrink-0">
                  <Users className="text-mainBrandColor w-6 h-6" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-logoHeading">Customer Profiles</h2>
                  <p className="text-xs text-iconColor">Select any customer from the list below to view and manage their profile details.</p>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-3">
                {isLoading ? (
                  <p className="text-xs text-iconColor p-8 text-center bg-white rounded-2xl border border-gray-100">Loading customer list...</p>
                ) : customerList?.data?.data?.length === 0 ? (
                  <p className="text-xs text-iconColor p-8 text-center bg-white rounded-2xl border border-gray-100">No customers found.</p>
                ) : (
                  customerList?.data?.data?.map((customer: any) => (
                    <div
                      key={customer.userId}
                      onClick={() => setSelectedUserId(customer.userId)}
                      className="p-4 rounded-2xl border border-transparent hover:border-accent/40 bg-white transition-all duration-200 cursor-pointer flex flex-col md:flex-row justify-between items-start md:items-center gap-4 group shadow-sm hover:shadow-md hover:scale-[1.005]"
                    >
                      {/* Left: Avatar & Basic Info */}
                      <div className="min-w-0 flex-1 text-left">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-xl bg-[#f9f9f9] border text-mainBrandColor font-bold text-sm flex items-center justify-center shrink-0">
                            {customer.customerName?.charAt(0) || "C"}
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <h4 className="text-sm font-bold text-logoHeading group-hover:text-mainBrandColor transition-colors">
                                {customer.customerName}
                              </h4>
                              {customer.createdOn && (
                                <span className="inline-flex items-center gap-1 text-[11px] font-medium text-slate-500 bg-slate-100 px-2 py-0.5 rounded-md">
                                  <Calendar className="w-3 h-3 text-slate-400" />
                                  Joined {formatDate(customer.createdOn)}
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-iconColor mt-0.5">
                              {customer.email || customer.phoneNumber || "No contact info saved"}
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Right: Balances & Navigation */}
                      <div className="flex items-center gap-4 sm:gap-6 shrink-0 w-full md:w-auto justify-between md:justify-end border-t md:border-t-0 pt-2 md:pt-0">
                        <div className="text-left md:text-right">
                          <p className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold">Wallet</p>
                          <p className="text-sm font-bold text-logoHeading group-hover:text-mainBrandColor transition-colors">
                            {customer.walletBalance ?? 0}
                          </p>
                        </div>

                        <div className="text-left md:text-right">
                          <p className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold">Petland Points</p>
                          <p className="text-sm font-bold text-logoHeading group-hover:text-mainBrandColor transition-colors">
                            {customer.rewardPoints ?? 0}
                          </p>
                        </div>

                        <ChevronRight className="w-4 h-4 text-iconColor transition-transform group-hover:translate-x-0.5 ml-2" />
                      </div>
                    </div>
                  ))
                )}
              </div>

              {customerList?.data && (
                <div className="flex items-center justify-between mt-6 border-t pt-4">
                  <p className="text-sm text-gray-500">
                    Showing page {page} of {totalPages} ({totalRecords} customers)
                  </p>

                  <div className="flex gap-2">
                    <CommonButton
                      text="Previous"
                      variant="outline"
                      disabled={page === 1 || isLoading}
                      onClick={() => setPage((p) => p - 1)}
                    />

                    <CommonButton
                      text="Next"
                      variant="filled"
                      disabled={page >= totalPages || isLoading}
                      onClick={() => setPage((p) => p + 1)}
                    />
                  </div>
                </div>
              )}
            </div>
          </>
        ) : (
          <div className="bg-white rounded-[2rem] border border-accent/20 shadow-2xl overflow-hidden min-h-[600px]">
            <Customer360Console
              userId={selectedUserId}
              onBack={() => setSelectedUserId(null)}
            />
          </div>
        )}

      </div>
    </div>
  );
};